import {
  FeedFilterCombinator,
  FeedFilterField,
  FeedFilterGroup,
  FeedFilterRule,
  FeedFilterRuleOperator,
  FeedFilterTree,
  FeedFilters,
} from './feed.model';

// UI-only helpers pro formulário de filtros avançados (SSAPP-5/SSAPP-20) —
// os tipos do contrato ficam em feed.model.ts (SSAPP-3), não redeclarados aqui.

export const FEED_FILTER_FIELD_OPTIONS: { label: string; value: FeedFilterField }[] = [
  { label: 'Legenda', value: 'caption' },
  { label: 'Hashtags', value: 'hashtags' },
  { label: 'Autor/criador', value: 'creator' },
];

export const FEED_FILTER_OPERATOR_OPTIONS: { label: string; value: FeedFilterRuleOperator }[] = [
  { label: 'Contém', value: 'contains' },
  { label: 'Não contém', value: 'notContains' },
];

export function createEmptyFeedFilterRule(): FeedFilterRule {
  return { field: 'hashtags', operator: 'contains', values: [] };
}

// SSAPP-20: um grupo novo já nasce com uma regra vazia — mesma sensação de
// uso do antigo botão único "Adicionar filtro" (clicar e já ver uma linha
// pronta pra preencher), só que agora escopada a esse grupo.
export function createEmptyFeedFilterGroup(): FeedFilterGroup {
  return { operator: 'and', rules: [createEmptyFeedFilterRule()] };
}

// Remove espaços nas pontas, valores vazios e duplicados (case-sensitive —
// o backend também não faz fold de acentos/caixa na comparação, ver
// filters-v1.md: "Sem remoção de acentos — só case-insensitive [na regra em
// si, não na normalização de entrada]").
export function normalizeFeedFilterValues(values: string[]): string[] {
  const trimmed = values.map(v => v.trim()).filter(v => v.length > 0);
  return Array.from(new Set(trimmed));
}

export function isFeedFilterRuleValid(rule: FeedFilterRule): boolean {
  return !!rule.field && !!rule.operator && normalizeFeedFilterValues(rule.values).length > 0;
}

export function isFeedFilterGroupValid(group: FeedFilterGroup): boolean {
  return group.rules.length > 0 && group.rules.every(isFeedFilterRuleValid);
}

// SSAPP-20: agora suporta múltiplos grupos — cada grupo combina suas regras
// com `group.operator`, e `topOperator` combina os grupos entre si. Permite
// montar lógica mista, ex. "Termo A" E "Termo B" OU "Termo C" via dois
// grupos (grupo 1: A e B com operator 'and'; grupo 2: C sozinho) e
// topOperator 'or'. groups: [] quando não sobra nenhuma regra em nenhum
// grupo, o que o avaliador do backend já trata como "sem filtro, aprova
// tudo" (MediaRuleEvaluator::hasEffectiveGroups).
export function serializeFeedFilters(
  groups: FeedFilterGroup[],
  topOperator: FeedFilterCombinator
): FeedFilterTree | undefined {
  const normalizedGroups: FeedFilterGroup[] = groups
    .map(group => ({
      operator: group.operator,
      rules: group.rules
        .map(rule => ({ ...rule, values: normalizeFeedFilterValues(rule.values) }))
        .filter(rule => rule.values.length > 0),
    }))
    .filter(group => group.rules.length > 0);

  if (normalizedGroups.length === 0) {
    return undefined;
  }

  return { version: 1, operator: topOperator, groups: normalizedGroups };
}

// SSAPP-7/SSAPP-20: leitura pro resumo/edição nos cards de feed já criados.
// `feed.filters` chega como `[]` (nunca `{}` nem `null` na prática, ver
// comentário em feed.model.ts) quando o feed não tem filtro configurado;
// `Array.isArray` cobre esse caso sem precisar checar `groups` antes.
export function getFeedFilterGroups(filters?: FeedFilters | null): FeedFilterGroup[] {
  if (!filters || Array.isArray(filters)) return [];
  return filters.groups ?? [];
}

export function getFeedFilterTopOperator(filters?: FeedFilters | null): FeedFilterCombinator {
  if (!filters || Array.isArray(filters)) return 'and';
  return filters.operator ?? 'and';
}

// Lista plana de regras de todos os grupos — só pra quem não precisa saber
// onde termina um grupo e começa o outro (ex. contagem total de regras).
export function getFeedFilterRules(filters?: FeedFilters | null): FeedFilterRule[] {
  return getFeedFilterGroups(filters).flatMap(group => group.rules);
}

const FIELD_LABELS: Record<FeedFilterField, string> = {
  caption: 'Legenda',
  hashtags: 'Hashtags',
  creator: 'Autor/criador',
};

const OPERATOR_LABELS: Record<FeedFilterRuleOperator, string> = {
  contains: 'contém',
  notContains: 'não contém',
};

const COMBINATOR_LABELS: Record<FeedFilterCombinator, string> = {
  and: 'E',
  or: 'OU',
};

/** Resumo curto de uma regra pra chip no card — "Hashtags contém "promo" +2". */
export function summarizeFeedFilterRule(rule: FeedFilterRule): string {
  const field = FIELD_LABELS[rule.field] ?? rule.field;
  const operator = OPERATOR_LABELS[rule.operator] ?? rule.operator;
  const values = rule.values ?? [];

  if (values.length === 0) return `${field} ${operator}`;

  const extra = values.length > 1 ? ` +${values.length - 1}` : '';
  return `${field} ${operator} "${values[0]}"${extra}`;
}

/**
 * SSAPP-20: resumo curto de um grupo pra chip no card. Um grupo com 1 regra
 * vira a própria regra ("Hashtags contém "promo""); com 2+ regras, vira as
 * regras entre parênteses combinadas pelo operador do grupo
 * ("(Hashtags contém "A" E Legenda contém "B")").
 */
export function summarizeFeedFilterGroup(group: FeedFilterGroup): string {
  const ruleSummaries = group.rules.map(summarizeFeedFilterRule);
  if (ruleSummaries.length <= 1) return ruleSummaries[0] ?? '';

  const joiner = ` ${COMBINATOR_LABELS[group.operator]} `;
  return `(${ruleSummaries.join(joiner)})`;
}
